import { useState, useRef } from 'react';
import useVideoDurationFix from '../hooks/useVideoDurationFix';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Play, X,
  CheckCircle, Loader2, AlertCircle,
  ThumbsUp, ThumbsDown, Bookmark, FileText, Lock, Info, Check,
  AlertTriangle, RotateCcw,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../utils/getInitials';
import { useInstructorReviewDetail } from '../hooks/cabin/useInstructorReviewDetail';
import { useScoreInterview } from '../hooks/cabin/useScoreInterview';
import { useAuthedVideo } from '../hooks/cabin/useAuthedVideo';
import { useResetInterviewCooldown } from '../hooks/cabin/useResetInterviewCooldown';
import ConfirmModal from '../components/ConfirmModal';
import useReducedMotion from '../hooks/useReducedMotion';
import {
  CCRI_BREAKDOWN_MAX,
  ARS_BREAKDOWN_MAX,
  BreakdownBar,
  getScoreColor,
} from '../lib/interviewScoreBands';

// ── F4 schema constants (CANONICAL — must not change) ─────────────────────────
const KP_KEYS = ['kp1_camera_gaze', 'kp2_smile', 'kp3_posture', 'kp4_body_language', 'kp5_energy', 'kp6_professional_appearance'];
const IL_KEYS = ['il1_diction', 'il2_voice_tone', 'il3_fluency', 'il4_clear_answer', 'il5_conciseness', 'il6_persuasion'];
const CK_KEYS = ['ck1_understanding', 'ck2_example_answer', 'ck3_profession_awareness', 'ck4_confidence', 'ck5_sincerity', 'ck6_no_memorization'];
const IN_KEYS = ['in1_pronunciation', 'in2_vocabulary', 'in3_sentence_structure', 'in4_fluency', 'in5_comprehensibility', 'in6_confidence'];
const ALL_F4_KEYS = [...KP_KEYS, ...IL_KEYS, ...CK_KEYS, ...IN_KEYS];

// Criteria excluded from CCRI/ARS score computation
const EXCLUDED_FROM_SCORE = new Set(['il5_conciseness', 'in6_confidence']);

function criteriaI18nKey(fullKey) {
  return fullKey.split('_')[0];
}

// Red-flag enum values (16 total — canonical)
const RED_FLAG_KEYS = [
  'poor_posture', 'grooming', 'unprofessional_appearance', 'eye_contact_avoidance',
  'low_energy', 'monotone_voice', 'language_barrier', 'memorized_answers',
  'nervousness_excessive', 'attitude', 'dishonesty_signs', 'safety_unawareness',
  'customer_service_gap', 'teamwork_concern', 'weak_motivation', 'punctuality',
];

const F4_GROUPS = [
  { id: 'kp', keys: KP_KEYS, badge: 'KP', badgeColor: 'bg-blue-100 text-blue-700 border-blue-300' },
  { id: 'il', keys: IL_KEYS, badge: 'IL', badgeColor: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  { id: 'ck', keys: CK_KEYS, badge: 'CK', badgeColor: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { id: 'in', keys: IN_KEYS, badge: 'IN', badgeColor: 'bg-amber-100 text-amber-700 border-amber-300' },
];

// ── Result options
const RESULT_OPTIONS = [
  { value: 'positive', labelKey: 'instructor.reviews.result.positive', color: 'emerald', Icon: ThumbsUp },
  { value: 'reserve',  labelKey: 'instructor.reviews.result.reserve',  color: 'amber',   Icon: Bookmark },
  { value: 'negative', labelKey: 'instructor.reviews.result.negative', color: 'rose',    Icon: ThumbsDown },
];

const RESULT_STYLES = {
  emerald: {
    active: 'bg-emerald-500 border-emerald-700 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)]',
    idle:   'bg-white border-gray-300 text-gray-600 hover:border-emerald-400',
  },
  amber: {
    active: 'bg-amber-500 border-amber-700 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)]',
    idle:   'bg-white border-gray-300 text-gray-600 hover:border-amber-400',
  },
  rose: {
    active: 'bg-rose-500 border-rose-700 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)]',
    idle:   'bg-white border-gray-300 text-gray-600 hover:border-rose-400',
  },
};

// ── Authenticated video player ─────────────────────────────────────────────────
function AuthedVideoPlayer({ sessionId, t }) {
  const { videoUrl, loading: videoLoading, error: videoError } = useAuthedVideo(sessionId, 'instructor');
  const videoElRef = useRef(null);
  // Duration fix: eğitmen tarafındaki WebM Infinity-duration sorununu gider
  useVideoDurationFix(videoElRef, videoUrl || null);

  if (videoLoading) {
    return (
      <div className="bg-gray-900 rounded-2xl border-2 border-gray-700 aspect-video flex items-center justify-center">
        <div className="text-center text-gray-400">
          <Loader2 size={28} className="mx-auto mb-2 animate-spin opacity-60" aria-hidden="true" />
          <p className="text-sm">{t('instructor.reviews.video_loading')}</p>
        </div>
      </div>
    );
  }

  if (videoError || !videoUrl) {
    return (
      <div className="bg-gray-100 border-2 border-gray-200 rounded-2xl aspect-video flex items-center justify-center">
        <div className="text-center text-gray-400">
          {videoError ? (
            <>
              <AlertCircle size={32} className="mx-auto mb-2 text-rose-400" aria-hidden="true" />
              <p className="text-sm text-rose-500">{t('instructor.reviews.video_error')}</p>
            </>
          ) : (
            <>
              <Play size={32} className="mx-auto mb-2 opacity-40" aria-hidden="true" />
              <p className="text-sm">{t('instructor.reviews.video_unavailable')}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden border-2 border-gray-700 aspect-video">
      <video
        ref={videoElRef}
        src={videoUrl}
        controls
        playsInline
        className="w-full h-full object-contain"
        aria-label={t('instructor.reviews.video_label')}
      />
    </div>
  );
}

// ── CV Summary collapse panel ──────────────────────────────────────────────────
function CVSummaryPanel({ cvProfile, t, reduced }) {
  const [open, setOpen] = useState(false);
  const panelId = 'cv-summary-panel';
  const hasCV = !!cvProfile;

  const sections = [
    { key: 'kisisel',      label: t('instructor.reviews.f4.cv_section_kisisel') },
    { key: 'egitim',       label: t('instructor.reviews.f4.cv_section_egitim') },
    { key: 'is_deneyimi',  label: t('instructor.reviews.f4.cv_section_deneyim') },
    { key: 'dil',          label: t('instructor.reviews.f4.cv_section_dil') },
    { key: 'motivasyon',   label: t('instructor.reviews.f4.cv_section_motivasyon') },
  ];

  return (
    <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
      <button
        type="button"
        data-testid="f4-cv-summary-toggle"
        disabled={!hasCV}
        onClick={() => hasCV && setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center justify-between px-4 py-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors hover:bg-gray-50"
      >
        <span className="flex items-center gap-2 font-bold text-sm text-gray-700">
          {hasCV ? (
            <FileText size={16} className="text-[var(--brand-primary)]" aria-hidden="true" />
          ) : (
            <Lock size={16} className="text-gray-400" aria-hidden="true" />
          )}
          {t('instructor.reviews.f4.cv_panel_label')}
          {hasCV && cvProfile?.status === 'approved' && (
            <span className="ml-1 px-1.5 py-0.5 text-xs font-bold rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
              {t('instructor.reviews.f4.cv_approved_badge')}
            </span>
          )}
        </span>
        <X
          size={16}
          className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-45' : 'rotate-0'}`}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            role="region"
            aria-label={t('instructor.reviews.f4.cv_panel_label')}
            initial={reduced ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduced ? false : { height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 flex flex-col gap-3 border-t border-gray-100 pt-3">
              {!hasCV ? (
                <p className="text-sm text-gray-400 italic">
                  {t('instructor.reviews.f4.cv_panel_empty')}
                </p>
              ) : (
                sections.map(({ key, label }) => {
                  const sectionData = cvProfile[key];
                  if (!sectionData) return null;
                  const entries = typeof sectionData === 'object' && !Array.isArray(sectionData)
                    ? Object.entries(sectionData).filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
                    : null;
                  return (
                    <div key={key}>
                      <p className="text-xs font-black text-gray-500 tracking-wide mb-1">{label}</p>
                      {entries && entries.length > 0 ? (
                        <div className="space-y-0.5">
                          {entries.map(([field, val]) => (
                            <div key={field} className="flex gap-2 text-xs">
                              {/* [3.1 fix] i18n key'e bak, yoksa okunabilir fallback (ham snake_case gösterilmez) */}
                              <span className="text-gray-400 shrink-0">
                                {(() => {
                                  const ikey = `cv.field.${field}`;
                                  const translated = t(ikey);
                                  if (translated && translated !== ikey) return translated;
                                  // Fallback: snake_case'i kelime kelime kapitalize et
                                  return field
                                    .split('_')
                                    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                                    .join(' ');
                                })()}:
                              </span>
                              <span className="text-gray-700 font-medium">
                                {typeof val === 'boolean'
                                  ? (val ? t('cabin.common.yes') : t('cabin.common.no'))
                                  : String(val)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">
                          {key === 'is_deneyimi' ? t('instructor.reviews.f4.cv_section_no_exp') : '—'}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Hybrid slider for 1-10 criteria ───────────────────────────────────────────
function CriteriaSlider({ fullKey, value, onChange, t, excluded }) {
  const shortKey = criteriaI18nKey(fullKey);
  const label = t(`instructor.reviews.f4.criteria.${shortKey}`);
  const ticks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <label
            htmlFor={`f4-slider-${fullKey}`}
            className="text-sm font-semibold text-gray-700 leading-tight"
          >
            {label}
          </label>
          {excluded && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-bold rounded-full bg-gray-100 border border-gray-200 text-gray-500"
              title={t('instructor.reviews.f4.score_excluded_hint')}
              aria-label={`${label} — ${t('instructor.reviews.f4.score_excluded_badge')}`}
            >
              <Info size={10} aria-hidden="true" />
              {t('instructor.reviews.f4.score_excluded_badge')}
            </span>
          )}
        </div>
        <span
          className="text-xl font-black text-[var(--brand-primary)] w-8 text-right shrink-0 tabular-nums"
          aria-hidden="true"
        >
          {value}
        </span>
      </div>
      <input
        id={`f4-slider-${fullKey}`}
        type="range"
        min={1}
        max={10}
        step={1}
        value={value}
        onChange={(e) => onChange(fullKey, Number(e.target.value))}
        className="w-full h-3 rounded-full cursor-pointer"
        style={{ accentColor: 'var(--brand-primary)' }}
        aria-label={`${label} — ${value}/10`}
        aria-valuemin={1}
        aria-valuemax={10}
        aria-valuenow={value}
      />
      <div className="flex justify-between text-xs text-gray-300 mt-0.5 px-px select-none" aria-hidden="true">
        {ticks.map(n => <span key={n}>{n}</span>)}
      </div>
    </div>
  );
}

// ── F4 group card ──────────────────────────────────────────────────────────────
function F4GroupCard({ group, f1Criteria, onChange, t }) {
  return (
    <div
      className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-5"
      data-testid={`f4-group-${group.id}`}
    >
      <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
        <span className={`text-xs font-black px-2 py-0.5 rounded-full border ${group.badgeColor}`}>
          {group.badge}
        </span>
        <h3 className="text-xs font-bold text-gray-600 tracking-wide">
          {t(`instructor.reviews.f4.group_${group.id}_title`)}
        </h3>
      </div>
      {group.keys.map((key) => (
        <CriteriaSlider
          key={key}
          fullKey={key}
          value={f1Criteria[key]}
          onChange={onChange}
          t={t}
          excluded={EXCLUDED_FROM_SCORE.has(key)}
        />
      ))}
    </div>
  );
}

// ── CV / Career score segmented radiogroup ─────────────────────────────────────
function CVScoreRadio({ value, onChange, t }) {
  const options = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-3">
      <div>
        <p className="text-xs font-bold text-gray-600 tracking-wide mb-0.5">
          {t('instructor.reviews.f4.cv_score_section_title')}
        </p>
        <p className="text-xs text-gray-500">{t('instructor.reviews.f4.cv_score_hint')}</p>
      </div>
      <div
        role="radiogroup"
        aria-label={t('instructor.reviews.f4.cv_score_label')}
        data-testid="f4-cv-score-radiogroup"
        className="flex flex-wrap gap-1"
      >
        {options.map((n) => {
          const isChecked = value === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={isChecked}
              onClick={() => onChange(n)}
              className={`min-h-[44px] min-w-[44px] flex-1 rounded-xl border-2 font-bold text-sm transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]
                ${isChecked
                  ? 'bg-[var(--brand-primary)] border-[var(--brand-primary-shadow)] text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,0.12)]'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-[var(--brand-primary)]'
                }`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-gray-400 italic">{t('instructor.reviews.f4.cv_score_ars_note')}</p>
    </div>
  );
}

// ── Red-flag section ───────────────────────────────────────────────────────────
function RedFlagSection({ selected, onToggle, note, onNoteChange, t }) {
  return (
    <div
      className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-4"
      data-testid="f4-redflag-section"
    >
      <div>
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-0.5">
          {t('instructor.reviews.f4.flag_section_title')}
        </p>
        <div className="flex items-start gap-1.5 mt-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Info size={13} className="text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-xs text-amber-700 font-medium">{t('instructor.reviews.f4.flag_disclaimer')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {RED_FLAG_KEYS.map((flagKey) => {
          const isChecked = selected.includes(flagKey);
          return (
            <label
              key={flagKey}
              data-testid={`flag-${flagKey}`}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all min-h-[44px]
                ${isChecked
                  ? 'bg-rose-50 border-rose-300'
                  : 'bg-white border-gray-200 hover:border-rose-200'
                }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={isChecked}
                onChange={() => onToggle(flagKey)}
                aria-checked={isChecked}
              />
              <span
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all
                  ${isChecked ? 'bg-rose-500 border-rose-600' : 'bg-white border-gray-300'}`}
                aria-hidden="true"
              >
                {isChecked && <Check size={12} className="text-white" />}
              </span>
              <span className={`text-xs font-semibold leading-snug ${isChecked ? 'text-rose-700' : 'text-gray-600'}`}>
                {t(`instructor.reviews.f4.flag.${flagKey}`)}
              </span>
            </label>
          );
        })}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="f4-flag-note" className="text-xs font-bold text-gray-600 uppercase tracking-wide">
          {t('instructor.reviews.f4.flag_note_label')}
        </label>
        <textarea
          id="f4-flag-note"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          rows={3}
          placeholder={t('instructor.reviews.f4.flag_note_placeholder')}
          className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-700 resize-none focus:outline-none focus:border-rose-400 transition-colors"
        />
      </div>
    </div>
  );
}

// ── Result segmented control ───────────────────────────────────────────────────
function ResultControl({ value, onChange, t }) {
  return (
    <div
      role="radiogroup"
      aria-label={t('instructor.reviews.result_label')}
      className="flex gap-2 flex-wrap"
    >
      {RESULT_OPTIONS.map(({ value: optVal, labelKey, color, Icon }) => {
        const isActive = value === optVal;
        const styles = RESULT_STYLES[color];
        return (
          <motion.button
            key={optVal}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(optVal)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className={`flex-1 min-w-[100px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 font-bold text-sm transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] ${isActive ? styles.active : styles.idle}`}
          >
            <Icon size={16} aria-hidden="true" />
            {t(labelKey)}
          </motion.button>
        );
      })}
    </div>
  );
}

// BreakdownBar — imported from ../lib/interviewScoreBands

// ── Score result panel (post-submit) ──────────────────────────────────────────
// Badge color and label driven by getScoreColor(pct) — 3-tier rule (≥80 high, ≥60 mid, <60 low).
// BreakdownBar already uses getScoreColor internally — breakdown bars auto-correct.
function ScoreResultPanel({ ccriArs, t, reduced }) {
  if (!ccriArs) return null;

  const {
    ccri_total, ccri_band: backendCCRIBand, ccri_breakdown,
    ars_total, ars_band: backendARSBand, ars_breakdown,
    excluded_areas,
  } = ccriArs;

  // excluded_areas: TR mülakatta ["ccri.ingilizce","ars.ingilizce_cevap"] gelir.
  // EN mülakatta veya eski kayıtlarda boş/undefined → tüm satırlar görünür.
  const excludedSet = Array.isArray(excluded_areas) ? new Set(excluded_areas) : new Set();

  // Tier-based colors (new 3-kademe pct rule)
  const ccriColor = getScoreColor(ccri_total != null ? ccri_total : 0);
  const arsColor  = getScoreColor(ars_total  != null ? ars_total  : 0);

  const ccriBreakdownEntries = ccri_breakdown
    ? Object.entries(ccri_breakdown).filter(([key]) => !excludedSet.has(`ccri.${key}`))
    : [];
  const arsBreakdownEntries = ars_breakdown
    ? Object.entries(ars_breakdown).filter(([key]) => !excludedSet.has(`ars.${key}`))
    : [];

  return (
    <motion.div
      data-testid="f4-score-result-panel"
      initial={reduced ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="flex flex-col gap-4"
    >
      {/* CCRI card */}
      <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-4">
        <h3 className="text-sm font-black text-gray-800">{t('instructor.reviews.f4.result_ccri_title')}</h3>
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <p
              className="text-5xl font-black tabular-nums"
              style={{ color: ccriColor.text }}
              aria-label={`CCRI ${ccri_total != null ? ccri_total : '—'} ${t('instructor.reviews.f4.result_out_of_100')}`}
            >
              {ccri_total != null ? Math.round(ccri_total) : '—'}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{t('instructor.reviews.f4.result_out_of_100')}</p>
          </div>
          <span
            role="status"
            aria-live="polite"
            className="px-3 py-1.5 rounded-full border-2 text-sm font-black"
            style={{ background: ccriColor.bg, borderColor: ccriColor.border, color: ccriColor.text }}
          >
            {t(ccriColor.labelKey) || backendCCRIBand}
          </span>
        </div>
        {ccriBreakdownEntries.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-bold text-gray-500 tracking-wide">
              {t('instructor.reviews.f4.result_breakdown_title')}
            </p>
            {ccriBreakdownEntries.map(([key, val]) => (
              <BreakdownBar
                key={key}
                label={t(`instructor.reviews.f4.breakdown.${key}`) || key}
                value={val != null ? Math.round(val) : 0}
                max={CCRI_BREAKDOWN_MAX[key] ?? 100}
                reduced={reduced}
              />
            ))}
          </div>
        )}
      </div>

      {/* ARS card */}
      <div className="bg-white border-2 border-gray-200 rounded-2xl p-5 flex flex-col gap-4">
        <h3 className="text-sm font-black text-gray-800">{t('instructor.reviews.f4.result_ars_title')}</h3>
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <p
              className="text-5xl font-black tabular-nums"
              style={{ color: arsColor.text }}
              aria-label={`ARS ${ars_total != null ? ars_total : '—'} ${t('instructor.reviews.f4.result_out_of_100')}`}
            >
              {ars_total != null ? Math.round(ars_total) : '—'}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{t('instructor.reviews.f4.result_out_of_100')}</p>
          </div>
          <span
            role="status"
            aria-live="polite"
            className="px-3 py-1.5 rounded-full border-2 text-sm font-black"
            style={{ background: arsColor.bg, borderColor: arsColor.border, color: arsColor.text }}
          >
            {t(arsColor.labelKey) || backendARSBand}
          </span>
        </div>
        {arsBreakdownEntries.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-bold text-gray-500 tracking-wide">
              {t('instructor.reviews.f4.result_breakdown_title')}
            </p>
            {arsBreakdownEntries.map(([key, val]) => (
              <BreakdownBar
                key={key}
                label={t(`instructor.reviews.f4.breakdown.${key}`) || key}
                value={val != null ? Math.round(val) : 0}
                max={ARS_BREAKDOWN_MAX[key] ?? 100}
                reduced={reduced}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ── F4 Scoring Form ────────────────────────────────────────────────────────────
// KEY NOTE: keyed by sessionId from parent; state reset happens via React key.
// submit payload (f1Criteria, result, cvCareerScore, redFlags, redFlagNote) is
// CANONICAL — matches backend test expectations. DO NOT alter field names.
//
// LAYOUT (Düzen 1 — Eğitmen Değerlendirme/İnceleme):
//   Sol kolon (flex ~2.4, baskın): büyük video (16:9) + sorular kartı + CV özeti
//   Sağ kolon (sabit ~430px): F4 form ya da skor kırılımı (readOnly)
//   Üst bar: navy, korunur.
function F4ScoringForm({ sessionId, detail, onScored, t, initialScoring }) {
  const toast = useToast();
  const { score: submitScore, loading: scoreLoading } = useScoreInterview();
  const reduced = useReducedMotion();

  // Dil filtresi: TR mülakatta IN grubu (İngilizce kriterleri) gizlenir ve skor dışına alınır
  const isTrInterview = (detail?.language || 'tr').toLowerCase() === 'tr';
  const activeGroups = isTrInterview
    ? F4_GROUPS.filter(g => g.id !== 'in')
    : F4_GROUPS;
  const activeF4Keys = activeGroups.flatMap(g => g.keys);

  // F4 state — initialised once on mount (keyed by sessionId)
  // Edit modunda initialScoring değerleri ön yüklenir
  const buildInitialF1 = () => {
    const base = Object.fromEntries(ALL_F4_KEYS.map(k => [k, 5]));
    if (initialScoring?.f1_criteria) {
      Object.assign(base, initialScoring.f1_criteria);
    } else if (initialScoring?.criteria) {
      Object.assign(base, initialScoring.criteria);
    }
    return base;
  };
  const [f1Criteria, setF1Criteria] = useState(buildInitialF1);
  const [result, setResult] = useState(initialScoring?.result || null);
  const [cvCareerScore, setCvCareerScore] = useState(initialScoring?.cv_career_score ?? 5);
  const [redFlags, setRedFlags] = useState(initialScoring?.red_flags || []);
  const [redFlagNote, setRedFlagNote] = useState(initialScoring?.red_flag_note || '');
  const [resultError, setResultError] = useState(false);
  const [ccriArsResult, setCcriArsResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // scoredKeys: activeF4Keys에서 EXCLUDED_FROM_SCORE çıkar
  const scoredKeys = activeF4Keys.filter(k => !EXCLUDED_FROM_SCORE.has(k));

  // Unscored warning: criteria still at initial value (5) are considered "not yet scored"
  const DEFAULT_SCORE = 5;
  const unscoredCount = scoredKeys.filter(k => f1Criteria[k] === DEFAULT_SCORE).length;

  const handleCriteriaChange = (key, val) => {
    const num = Math.min(10, Math.max(1, Number(val)));
    setF1Criteria(prev => ({ ...prev, [key]: num }));
  };

  const handleFlagToggle = (flagKey) => {
    setRedFlags(prev =>
      prev.includes(flagKey) ? prev.filter(f => f !== flagKey) : [...prev, flagKey]
    );
  };

  const handleResultChange = (val) => {
    setResult(val);
    if (val) setResultError(false);
  };

  const handleSubmit = async () => {
    if (!result) {
      setResultError(true);
      if (toast && typeof toast.error === 'function') {
        toast.error(t('instructor.reviews.result_required'));
      }
      return;
    }
    setResultError(false);

    // CANONICAL payload — backend regression tests depend on these field names
    // TR mülakatda IN kriterleri (in1-in6) gönderilmez; backend zaten renormalize ediyor
    const activeCriteria = Object.fromEntries(
      Object.entries(f1Criteria).filter(([k]) => activeF4Keys.includes(k))
    );
    const res = await submitScore({
      sessionId,
      f1Criteria: activeCriteria,
      result,
      cvCareerScore,
      redFlags,
      redFlagNote,
    });

    if (!res) {
      if (toast && typeof toast.error === 'function') {
        toast.error(t('instructor.reviews.score_error'));
      }
      return;
    }
    if (toast && typeof toast.success === 'function') {
      toast.success(t('instructor.reviews.score_success'));
    }
    if (res.ccri_ars) {
      setCcriArsResult(res.ccri_ars);
      setSubmitted(true);
    } else {
      // Legacy schema: navigate immediately
      onScored(res.total_score);
    }
  };

  const cvProfile = detail?.cv_profile || null;

  return (
    /* Düzen 1: Sol baskın (video büyük + sorular), sağ sabit panel (form/skor) */
    <div className="flex flex-col md:flex-row md:items-start" style={{ gap: 20 }}>

      {/* SOL KOLON — baskın, video büyük (flex ~2.4) */}
      <div
        style={{ flex: '2.4 1 0%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
        className="md:sticky md:top-[78px]"
      >
        {/* Büyük video — 16:9 korunur, sol alanın tamamını kaplar */}
        <AuthedVideoPlayer sessionId={sessionId} t={t} />

        {/* SORULAR kartı — video genişliğiyle hizalı, hemen altında */}
        {detail?.questions && detail.questions.length > 0 && (
          <div
            style={{
              background: '#fff',
              border: '2px solid #E5EAF0',
              borderRadius: 16,
              padding: '16px 18px',
              boxShadow: '0 2px 10px rgba(16,40,73,.04)',
            }}
          >
            <p
              style={{
                fontSize: 11, fontWeight: 800, letterSpacing: '.6px',
                color: '#8A9BB0', textTransform: 'uppercase', marginBottom: 12,
              }}
            >
              {t('cabin.interview.video.question_list')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {detail.questions.map((q, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span
                    style={{
                      minWidth: 24, height: 24, borderRadius: 8,
                      background: 'var(--brand-primary)', color: '#fff',
                      fontSize: 11, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, marginTop: 1,
                    }}
                  >
                    {i + 1}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13.5, fontWeight: 600, color: '#1E2E44', lineHeight: 1.45, margin: 0 }}>
                      {q.question}
                    </p>
                    {q.category && (
                      <span
                        style={{
                          display: 'inline-block', marginTop: 4,
                          fontSize: 11, fontWeight: 700,
                          background: '#F0F5FA', border: '1px solid #D8E2EE',
                          borderRadius: 99, padding: '2px 10px', color: '#5B7292',
                        }}
                      >
                        {(() => {
                          const key = `interview.sim.cat.${q.category}`;
                          const tr = t(key);
                          return (tr && tr !== key) ? tr : (t('interview.sim.cat.general') || 'General');
                        })()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CV özeti — en altta */}
        <CVSummaryPanel cvProfile={cvProfile} t={t} reduced={reduced} />
      </div>

      {/* SAĞ KOLON — sabit genişlik (430px geniş ekran), dar ekranda tam genişlik */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
        className="w-full md:w-[430px]"
      >
        <AnimatePresence mode="wait">
          {submitted && ccriArsResult ? (
            <motion.div
              key="result"
              initial={reduced ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex flex-col gap-4"
            >
              <ScoreResultPanel ccriArs={ccriArsResult} t={t} reduced={reduced} />
              <motion.button
                type="button"
                onClick={() => onScored(null)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-3 rounded-2xl bg-gray-100 border-2 border-gray-300 text-gray-700 font-bold text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] flex items-center justify-center gap-2"
              >
                <CheckCircle size={16} aria-hidden="true" />
                {t('instructor.reviews.score_success')}
              </motion.button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={reduced ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-4"
            >
              {/* F4 group cards — TR mülakatda IN grubu gösterilmez */}
              {activeGroups.map((group) => (
                <F4GroupCard
                  key={group.id}
                  group={group}
                  f1Criteria={f1Criteria}
                  onChange={handleCriteriaChange}
                  t={t}
                />
              ))}

              {/* CV / Career score */}
              <CVScoreRadio value={cvCareerScore} onChange={setCvCareerScore} t={t} />

              {/* Red flags */}
              <RedFlagSection
                selected={redFlags}
                onToggle={handleFlagToggle}
                note={redFlagNote}
                onNoteChange={setRedFlagNote}
                t={t}
              />

              {/* Result decision */}
              <div
                className={`rounded-2xl border-2 p-5 flex flex-col gap-3 transition-colors ${
                  resultError ? 'bg-rose-50 border-rose-400' : 'bg-white border-gray-200'
                }`}
              >
                <p className={`text-xs font-bold uppercase tracking-wide ${resultError ? 'text-rose-600' : 'text-gray-600'}`}>
                  {t('instructor.reviews.result_label')}
                  {resultError && (
                    <span className="ml-2 text-rose-600 normal-case font-semibold text-xs">
                      — {t('instructor.reviews.result_required')}
                    </span>
                  )}
                </p>
                <ResultControl value={result} onChange={handleResultChange} t={t} />
              </div>

              {/* Unscored criteria warning — shown when >0 criteria are still at default (5) */}
              {unscoredCount > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border-2 border-amber-200 rounded-xl px-4 py-3" role="alert">
                  <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-xs text-amber-800 font-semibold leading-snug">
                    {t('instructor.reviews.f4.unscored_warning').replace('{{count}}', unscoredCount)}
                  </p>
                </div>
              )}

              {/* Submit — brand-primary (blue), NOT emerald. Green reserved for success toasts only. */}
              <motion.button
                type="button"
                data-testid="f4-submit-btn"
                onClick={handleSubmit}
                disabled={scoreLoading}
                whileHover={reduced ? {} : { scale: 1.02 }}
                whileTap={reduced ? {} : { scale: 0.98 }}
                className="w-full py-4 rounded-2xl bg-[var(--brand-primary)] border-2 border-[var(--brand-primary-shadow)] text-white font-bold text-base shadow-[2px_2px_0px_0px_rgba(0,0,0,0.15)] disabled:opacity-50 hover:opacity-90 transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px] flex items-center justify-center gap-2"
              >
                {scoreLoading
                  ? <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> {t('instructor.reviews.scoring')}</>
                  : <><CheckCircle size={18} aria-hidden="true" /> {t('instructor.reviews.submit_score')}</>
                }
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Legacy scored read-only card (schema_version 1 or 2) ─────────────────────
function LegacyScoredCard({ detail, t }) {
  const version = detail?.scoring?.schema_version ?? 1;
  const scoring = detail?.scoring || {};
  const criteria = scoring.criteria || {};
  const result = scoring.result || detail?.result;
  const totalScore = scoring.total_score ?? detail?.total_score;
  const maxScore = scoring.max_score ?? detail?.max_score;

  return (
    <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <AlertCircle size={16} className="text-amber-600" aria-hidden="true" />
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">
          {t('instructor.reviews.f4.legacy_readonly_title')} — v{version}
        </p>
      </div>

      {totalScore != null && (
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-black text-amber-700 tabular-nums">{totalScore}</span>
          <span className="text-sm text-amber-600">/ {maxScore ?? (version === 1 ? 50 : 65)}</span>
        </div>
      )}

      {result && (
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-gray-500">{t('instructor.reviews.result_label')}:</span>
          <span className="px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-700">
            {t(`instructor.reviews.result.${result}`)}
          </span>
        </div>
      )}

      {Object.keys(criteria).length > 0 && (
        <div className="flex flex-col gap-2">
          {Object.entries(criteria).map(([key, val]) => (
            <div key={key} className="flex items-center justify-between text-xs">
              <span className="text-gray-600 font-medium">
                {t(`instructor.reviews.criteria.${key}`) || key}
              </span>
              <span className="font-black text-amber-700 tabular-nums">{val}</span>
            </div>
          ))}
        </div>
      )}

      {scoring.feedback && (
        <div className="bg-white border border-amber-200 rounded-xl p-3 text-xs text-gray-600 italic">
          {scoring.feedback}
        </div>
      )}
    </div>
  );
}

/**
 * InstructorInterviewScoring — /instructor/interview-reviews/:sessionId/score
 *
 * Ekran 6: Eğitmen için F4 puanlama formu tam ekran route.
 * InstructorInterviewReviews'daki "İncele/Puanla" aksiyonu buraya navigate eder.
 * Submit sonrası listeye geri döner ve refetch sinyali location.state aracılığıyla iletilir.
 * 24-kriter F4 rubric AYNEN korunmuştur.
 */
export default function InstructorInterviewScoring() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user } = useAuth();

  // readOnly: mod artık URL query param üzerinden taşınır (?mode=edit | ?mode=view).
  // Yenileme / derin-link sonrası mod korunur; kasıtsız edit'e düşme riski yoktur.
  // Eski location.state yaklaşımı geriye dönük uyumluluk için ikincil olarak desteklenir.
  const modeParam = searchParams.get('mode');
  const readOnly = modeParam === 'edit'
    ? false
    : modeParam === 'view'
      ? true
      : location.state == null
        ? true
        : location.state.readOnly !== false;

  const { detail, loading } = useInstructorReviewDetail(sessionId);
  const toast = useToast();

  // ── Cooldown reset ────────────────────────────────────────────────────────
  const canResetCooldown = ['instructor', 'admin', 'superadmin'].includes(user?.role);
  const [cooldownModalOpen, setCooldownModalOpen] = useState(false);
  const resetCooldownMutation = useResetInterviewCooldown();

  const handleResetCooldownConfirm = async () => {
    const userId = detail?.user_id;
    if (!userId) return;
    try {
      await resetCooldownMutation.mutateAsync(userId);
      setCooldownModalOpen(false);
      if (toast && typeof toast.success === 'function') {
        toast.success(t('interview.sim.admin.reset_cooldown_success'));
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message_tr ||
        err?.response?.data?.message_en ||
        err?.response?.data?.detail ||
        t('interview.sim.admin.reset_cooldown_error');
      if (toast && typeof toast.error === 'function') {
        toast.error(msg);
      }
    }
  };

  const isScored = detail?.status === 'scored';
  const schemaVersion = detail?.scoring?.schema_version ?? detail?.schema_version ?? null;
  const isF4Scored = isScored && schemaVersion === 3;
  // schemaVersion===null is NOT treated as legacy — it may be an unrecognised future schema.
  // Legacy = explicitly v1 or v2.
  const isLegacyScored = isScored && !isF4Scored && schemaVersion !== null;
  // schemaVersion===null with a scored doc → unknown schema, falls through to F4 form (read-only guard below).
  const isUnknownSchema = isScored && schemaVersion === null;

  const handleBack = () => {
    navigate('/instructor/interview-reviews');
  };

  // Called after successful score submission — navigate back and signal refetch
  const handleScored = () => {
    navigate('/instructor/interview-reviews', { state: { refetch: true, scoredSessionId: sessionId } });
  };

  const studentInitials = getInitials(detail?.user_name || '');

  return (
    <div className="min-h-screen" style={{ background: '#F4F7FA' }}>
      {/* Proto-style full-screen header — dark navy, 66px */}
      <header
        style={{
          height: 66, flexShrink: 0,
          background: '#13233F',
          borderBottom: '1px solid rgba(255,255,255,.08)',
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '0 24px',
          color: '#fff',
          position: 'sticky', top: 0, zIndex: 20,
        }}
      >
        <button
          type="button"
          onClick={handleBack}
          aria-label={t('cabin.interview.text.back')}
          style={{
            width: 40, height: 40, borderRadius: 11,
            border: '1px solid rgba(255,255,255,.14)',
            background: 'rgba(255,255,255,.06)',
            color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>

        {detail && (
          <div
            aria-hidden="true"
            style={{
              width: 38, height: 38, borderRadius: 11,
              background: 'linear-gradient(135deg,#1F3357,#27406B)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 14, flexShrink: 0,
            }}
          >
            {studentInitials}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Fredoka','Nunito',sans-serif", fontWeight: 600, fontSize: 17, lineHeight: 1.1, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {detail?.user_name || t('instructor.reviews.detail_title')}
          </div>
          <div style={{ fontSize: 12.5, color: '#9FB0C6', marginTop: 1 }}>
            {isScored
              ? `${t('interview.sim.status_scored')} · ${t('instructor.reviews.detail_title')}`
              : `${t('instructor.reviews.tab_pending')} · ${t('instructor.reviews.detail_title')}`
            }
          </div>
        </div>

        {/* Right side: status badge + cooldown reset */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {detail && isScored && (
            <span style={{
              background: '#E6F6EC', color: '#1F9A4D',
              fontWeight: 800, fontSize: 12.5,
              padding: '6px 14px', borderRadius: 99,
            }}>
              {t('interview.sim.status_scored')}
            </span>
          )}

          {/* Cooldown reset — instructor/admin only, only when student user_id is known */}
          {detail?.user_id && canResetCooldown && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => setCooldownModalOpen(true)}
              disabled={resetCooldownMutation.isPending}
              aria-label={t('interview.sim.admin.reset_cooldown_btn')}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 13px', borderRadius: 10,
                border: '1.5px solid rgba(251,191,36,.5)',
                background: 'rgba(251,191,36,.10)',
                color: '#FBBF24',
                fontFamily: 'inherit', fontWeight: 700, fontSize: 13,
                cursor: 'pointer', flexShrink: 0,
                opacity: resetCooldownMutation.isPending ? 0.5 : 1,
              }}
            >
              {resetCooldownMutation.isPending
                ? <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                : <RotateCcw size={15} aria-hidden="true" />
              }
              {t('interview.sim.admin.reset_cooldown_btn')}
            </motion.button>
          )}
        </div>
      </header>

      {/* Cooldown reset confirm modal */}
      <ConfirmModal
        isOpen={cooldownModalOpen}
        onClose={() => { if (!resetCooldownMutation.isPending) setCooldownModalOpen(false); }}
        onConfirm={handleResetCooldownConfirm}
        type="info"
        title={t('interview.sim.admin.reset_cooldown_confirm_title')}
        message={t('interview.sim.admin.reset_cooldown_confirm_desc')}
        submitting={resetCooldownMutation.isPending}
      />

      {/* Body */}
      <div className="w-full px-4 md:px-6 py-4 md:py-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !detail ? (
          <div className="text-center py-12 text-gray-400">
            <AlertCircle size={36} className="mx-auto mb-3 text-rose-400" aria-hidden="true" />
            <p className="font-medium mb-4">{t('cabin.interview.history.no_detail')}</p>
            <button
              type="button"
              onClick={handleBack}
              className="px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] border-2 border-[var(--brand-primary-shadow)] text-white text-sm font-bold hover:opacity-90 transition-opacity"
            >
              {t('cabin.interview.text.back')}
            </button>
          </div>
        ) : isF4Scored ? (
          // F4 zaten puanlandı:
          //   readOnly===true (İncele) → ScoreResultPanel salt-okunur
          //   readOnly===false (Düzenle) → F4ScoringForm mevcut skor değerleriyle pre-fill
          readOnly ? (
            <div className="flex flex-col md:flex-row md:items-start" style={{ gap: 20 }}>
              <div
                style={{ flex: '2.4 1 0%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
                className="md:sticky md:top-[78px]"
              >
                <AuthedVideoPlayer sessionId={sessionId} t={t} />
                {detail?.questions && detail.questions.length > 0 && (
                  <div
                    style={{
                      background: '#fff',
                      border: '2px solid #E5EAF0',
                      borderRadius: 16,
                      padding: '16px 18px',
                      boxShadow: '0 2px 10px rgba(16,40,73,.04)',
                    }}
                  >
                    <p
                      style={{
                        fontSize: 11, fontWeight: 800, letterSpacing: '.6px',
                        color: '#8A9BB0', textTransform: 'uppercase', marginBottom: 12,
                      }}
                    >
                      {t('cabin.interview.video.question_list')}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {detail.questions.map((q, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <span
                            style={{
                              minWidth: 24, height: 24, borderRadius: 8,
                              background: 'var(--brand-primary)', color: '#fff',
                              fontSize: 11, fontWeight: 800,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0, marginTop: 1,
                            }}
                          >
                            {i + 1}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 13.5, fontWeight: 600, color: '#1E2E44', lineHeight: 1.45, margin: 0 }}>
                              {q.question}
                            </p>
                            {q.category && (
                              <span
                                style={{
                                  display: 'inline-block', marginTop: 4,
                                  fontSize: 11, fontWeight: 700,
                                  background: '#F0F5FA', border: '1px solid #D8E2EE',
                                  borderRadius: 99, padding: '2px 10px', color: '#5B7292',
                                }}
                              >
                                {(() => {
                                  const key = `interview.sim.cat.${q.category}`;
                                  const tr = t(key);
                                  return (tr && tr !== key) ? tr : (t('interview.sim.cat.general') || 'General');
                                })()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ width: 430, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }} className="w-full md:w-[430px]">
                <ScoreResultPanel
                  ccriArs={detail?.scoring?.ccri_ars || detail?.ccri_ars || null}
                  t={t}
                  reduced={false}
                />
              </div>
            </div>
          ) : (() => {
            // Düzenle modu — yalnızca schema_version===3 ve f1_criteria mevcut ise açılır.
            // Eski v2 kayıtlarda f1_criteria yok; Object.assign F4 anahtarlarına uymaz → tüm
            // slider'lar default 5 yüklenir → kaydet = sessiz veri kaybı.
            // Guard: f1_criteria yoksa edit modunu engelle, read-only fallback + uyarı banner'ı göster.
            const hasFourthGen = !!(detail?.scoring?.f1_criteria);
            if (!hasFourthGen) {
              return (
                <div className="flex flex-col gap-4 max-w-xl mx-auto py-10">
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-6 flex flex-col gap-3 shadow-[2px_2px_0px_0px_rgba(0,0,0,0.10)]">
                    <div className="flex items-center gap-3">
                      <AlertTriangle size={22} className="text-amber-600 shrink-0" aria-hidden="true" />
                      <p className="font-bold text-amber-800 text-base leading-snug">
                        {t('instructor.reviews.v2_edit_disabled_title')}
                      </p>
                    </div>
                    <p className="text-sm text-amber-700 leading-relaxed">
                      {t('instructor.reviews.v2_edit_disabled_desc')}
                    </p>
                    <p className="text-xs text-amber-600 leading-relaxed">
                      {t('instructor.reviews.v2_edit_rescore_hint')}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-300 bg-white text-gray-700 text-sm font-bold hover:border-gray-400 transition-colors"
                    >
                      <ArrowLeft size={16} aria-hidden="true" />
                      {t('cabin.interview.text.back')}
                    </button>
                  </div>
                  {/* Read-only skor kırılımı — mevcut CCRI/ARS skorlarını göster */}
                  <ScoreResultPanel
                    ccriArs={detail?.scoring?.ccri_ars || detail?.ccri_ars || null}
                    t={t}
                    reduced={false}
                  />
                </div>
              );
            }
            // F4 (schema_version===3) + f1_criteria mevcut → normal edit modu
            return (
              <F4ScoringForm
                key={`${sessionId}-edit`}
                sessionId={sessionId}
                detail={detail}
                onScored={handleScored}
                t={t}
                initialScoring={detail?.scoring || null}
              />
            );
          })()
        ) : isLegacyScored ? (
          // Legacy scored (v1/v2) — read-only (Düzen 1: sol büyük video + sorular, sağ 430px legacy kart)
          <div className="flex flex-col md:flex-row md:items-start" style={{ gap: 20 }}>
            <div
              style={{ flex: '2.4 1 0%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
              className="md:sticky md:top-[78px]"
            >
              <AuthedVideoPlayer sessionId={sessionId} t={t} />
              {detail?.questions && detail.questions.length > 0 && (
                <div
                  style={{
                    background: '#fff',
                    border: '2px solid #E5EAF0',
                    borderRadius: 16,
                    padding: '16px 18px',
                    boxShadow: '0 2px 10px rgba(16,40,73,.04)',
                  }}
                >
                  <p
                    style={{
                      fontSize: 11, fontWeight: 800, letterSpacing: '.6px',
                      color: '#8A9BB0', textTransform: 'uppercase', marginBottom: 12,
                    }}
                  >
                    {t('cabin.interview.video.question_list')}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {detail.questions.map((q, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <span
                          style={{
                            minWidth: 24, height: 24, borderRadius: 8,
                            background: 'var(--brand-primary)', color: '#fff',
                            fontSize: 11, fontWeight: 800,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, marginTop: 1,
                          }}
                        >
                          {i + 1}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13.5, fontWeight: 600, color: '#1E2E44', lineHeight: 1.45, margin: 0 }}>
                            {q.question}
                          </p>
                          {q.category && (
                            <span
                              style={{
                                display: 'inline-block', marginTop: 4,
                                fontSize: 11, fontWeight: 700,
                                background: '#F0F5FA', border: '1px solid #D8E2EE',
                                borderRadius: 99, padding: '2px 10px', color: '#5B7292',
                              }}
                            >
                              {(() => {
                                const key = `interview.sim.cat.${q.category}`;
                                const tr = t(key);
                                return (tr && tr !== key) ? tr : (t('interview.sim.cat.general') || 'General');
                              })()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ width: 430, flexShrink: 0 }} className="w-full md:w-[430px]">
              <LegacyScoredCard detail={detail} t={t} />
            </div>
          </div>
        ) : isUnknownSchema ? (
          // Scored but schema_version is null — unrecognised schema, show safe fallback
          <div className="text-center py-12 text-gray-400">
            <AlertCircle size={36} className="mx-auto mb-3 text-amber-400" aria-hidden="true" />
            <p className="font-medium text-gray-600 mb-1">{t('instructor.reviews.unknown_schema_title')}</p>
            <p className="text-sm text-gray-400">{t('instructor.reviews.unknown_schema_desc')}</p>
          </div>
        ) : (
          // Pending — show F4 form
          // key=sessionId ensures full remount for new sessions, preventing stale controlled state
          <F4ScoringForm
            key={sessionId}
            sessionId={sessionId}
            detail={detail}
            onScored={handleScored}
            t={t}
          />
        )}
      </div>
    </div>
  );
}
